import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import Navbar from '@/components/Navbar';
import axios from 'axios';
import { Copy, Check, TrendingUp, Wallet, Users, DollarSign, Activity } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, Cell } from 'recharts';

const GREEN = '#4ade80';
const GREEN_DIM = 'rgba(74,222,128,0.12)';
const GREEN_BORDER = 'rgba(74,222,128,0.25)';
const ACCENT = '#9EAFAC';
const ACCENT_DARK = '#586060';
const BG = '#131413';

const formatDate = (value) => {
  if (!value) return 'Sin fecha';
  try {
    return new Date(value).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return 'Sin fecha';
  }
};

const statusLabel = (status) => status === 'active' ? 'Activo' : 'Pendiente';

const DirectNetworkCard = ({ directReferrals, activeCount, goal, completed, isMobile }) => (
  <div style={{ background: 'rgba(19,20,19,0.9)', border: `1px solid ${completed ? GREEN_BORDER : 'rgba(158,175,172,0.1)'}`, borderRadius: '20px', padding: '24px' }}>
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', marginBottom: '16px' }}>
      <div>
        <p style={{ color: ACCENT_DARK, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: '8px' }}>Mi red directa</p>
        <h3 style={{ color: 'white', fontFamily: 'Outfit, sans-serif', fontSize: isMobile ? '24px' : '28px', margin: 0 }}>{activeCount}/{goal} invitados activos</h3>
      </div>
      <div style={{ width: '42px', height: '42px', borderRadius: '12px', background: completed ? GREEN_DIM : 'rgba(158,175,172,0.08)', border: `1px solid ${completed ? GREEN_BORDER : 'rgba(158,175,172,0.14)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Users size={18} color={completed ? GREEN : ACCENT} />
      </div>
    </div>
    <p style={{ color: completed ? GREEN : ACCENT, fontSize: '14px', lineHeight: 1.5, marginBottom: '16px' }}>
      {completed ? 'Objetivo directo cumplido' : 'Invita a 2 personas con tu link personal para completar tu primer nivel.'}
    </p>
    {directReferrals.length === 0 ? (
      <div style={{ background: 'rgba(158,175,172,0.04)', border: '1px solid rgba(158,175,172,0.1)', borderRadius: '12px', padding: '14px', color: ACCENT_DARK, fontSize: '13px', lineHeight: 1.5 }}>
        Cuando alguien se registre con tu link, aparecerá aquí.
      </div>
    ) : (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {directReferrals.map(ref => (
          <div key={ref.id} className="referral-item" style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '10px', alignItems: 'center', padding: '10px 12px', borderRadius: '12px', background: 'rgba(158,175,172,0.04)', border: '1px solid rgba(158,175,172,0.08)' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ color: 'white', fontSize: '14px', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ref.full_name || 'Invitado'}</div>
              <div style={{ color: ACCENT_DARK, fontSize: '12px', marginTop: '2px' }}>{ref.referral_code || 'Sin código'} · {ref.investment_date ? `Activo desde ${formatDate(ref.investment_date)}` : formatDate(ref.created_at)}</div>
            </div>
            <span style={{ color: ref.investment_status === 'active' ? GREEN : ACCENT_DARK, background: ref.investment_status === 'active' ? GREEN_DIM : 'rgba(158,175,172,0.06)', border: `1px solid ${ref.investment_status === 'active' ? GREEN_BORDER : 'rgba(158,175,172,0.12)'}`, borderRadius: '999px', padding: '4px 9px', fontSize: '12px', fontWeight: 800 }}>
              {statusLabel(ref.investment_status)}
            </span>
          </div>
        ))}
      </div>
    )}
  </div>
);

const CompactVisualTree = ({ currentUser, directReferrals, visualNodes }) => {
  const byParent = {};
  const merged = new Map();
  [...directReferrals.map(ref => ({ ...ref, depth: 1, referred_by: currentUser?.id })), ...visualNodes].forEach(node => {
    if (!node?.id) return;
    merged.set(node.id, { ...merged.get(node.id), ...node });
  });
  Array.from(merged.values()).forEach(node => {
    const parentId = node.referred_by || (Number(node.depth) === 1 ? currentUser?.id : null);
    if (!parentId) return;
    byParent[parentId] = byParent[parentId] || [];
    byParent[parentId].push(node);
  });
  Object.values(byParent).forEach(children => children.sort((a, b) => (a.created_at || '').localeCompare(b.created_at || '')));

  const renderNode = (node, depth = 0) => {
    const children = byParent[node.id] || [];
    return (
      <div key={node.id}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 0', marginLeft: `${Math.min(depth, 4) * 18}px`, borderBottom: '1px solid rgba(158,175,172,0.07)' }}>
          <span style={{ color: ACCENT_DARK, width: '18px', fontSize: '13px' }}>{depth === 0 ? 'Tú' : depth === 1 ? '├' : '└'}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: 'white', fontSize: '14px', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{node.full_name || 'Invitado'}</div>
            <div style={{ color: ACCENT_DARK, fontSize: '12px' }}>{node.referral_code || 'Sin código'} · Nivel {depth}</div>
          </div>
          <span style={{ color: node.investment_status === 'active' ? GREEN : ACCENT_DARK, fontSize: '12px', fontWeight: 800 }}>{depth === 0 ? 'Root' : statusLabel(node.investment_status)}</span>
        </div>
        {children.slice(0, 8).map(child => renderNode(child, depth + 1))}
      </div>
    );
  };

  const rootNode = {
    id: currentUser?.id || 'current-user',
    full_name: currentUser?.full_name || 'Tú',
    referral_code: currentUser?.referral_code,
    investment_status: currentUser?.investment_status,
  };

  return (
    <div>
      {renderNode(rootNode, 0)}
      {(byParent[rootNode.id] || []).length === 0 && (
        <p style={{ color: ACCENT_DARK, fontSize: '13px', lineHeight: 1.5, marginTop: '12px' }}>Cuando alguien se registre con tu link, aparecerá debajo de tu nodo.</p>
      )}
    </div>
  );
};

const CustomTooltip = ({ active, payload }) => {
  if (active && payload?.length) {
    return (
      <div style={{ background: '#0d0e0d', border: '1px solid rgba(158,175,172,0.15)', borderRadius: '12px', padding: '8px 12px' }}>
        <div style={{ color: ACCENT, fontWeight: 700 }}>${parseFloat(payload[0].value).toFixed(2)}</div>
        <div style={{ color: ACCENT_DARK, fontSize: '12px' }}>{payload[0].payload.label}</div>
      </div>
    );
  }
  return null;
};

// Animated counter hook
function useAnimatedValue(target, duration = 800) {
  const [current, setCurrent] = useState(0);
  useEffect(() => {
    if (!target) return;
    const start = 0;
    const end = parseFloat(target);
    if (isNaN(end)) return;
    const startTime = Date.now();
    const step = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCurrent(start + (end - start) * eased);
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration]);
  return current;
}

// Animated stat card with glow
const AnimatedStatCard = ({ label, value, prefix = '', suffix = '', color = ACCENT, testid, icon: Icon, glowColor, delay = 0 }) => {
  const [visible, setVisible] = useState(false);
  const animValue = useAnimatedValue(visible ? parseFloat(value) || 0 : 0, 900);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(t);
  }, [delay]);

  const isGreen = color === GREEN;
  const displayValue = typeof value === 'number' ? animValue : value;
  const formattedValue = typeof value === 'number'
    ? (value % 1 !== 0 ? animValue.toFixed(2) : Math.round(animValue).toString())
    : value;

  return (
    <div
      data-testid={testid}
      style={{
        background: isGreen ? 'rgba(15,30,20,0.95)' : 'rgba(19,20,19,0.9)',
        border: `1px solid ${isGreen ? GREEN_BORDER : 'rgba(158,175,172,0.1)'}`,
        borderRadius: '16px',
        padding: '20px',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: isGreen ? `0 0 24px rgba(74,222,128,0.08)` : 'none',
        transition: 'box-shadow 0.3s',
      }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = isGreen ? `0 0 32px rgba(74,222,128,0.14)` : `0 0 20px rgba(158,175,172,0.06)`; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = isGreen ? `0 0 24px rgba(74,222,128,0.08)` : 'none'; }}
    >
      {/* Glow top accent */}
      {isGreen && (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg, transparent, rgba(74,222,128,0.5), transparent)' }} />
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <p style={{ color: isGreen ? 'rgba(74,222,128,0.6)' : ACCENT_DARK, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '8px' }}>{label}</p>
          <p style={{ color: isGreen ? GREEN : color, fontFamily: 'Outfit, sans-serif', fontSize: '26px', fontWeight: 700, margin: 0 }}>
            {prefix}{formattedValue}{suffix}
          </p>
        </div>
        {Icon && (
          <div style={{ padding: '8px', background: isGreen ? GREEN_DIM : 'rgba(158,175,172,0.08)', borderRadius: '10px' }}>
            <Icon size={16} color={isGreen ? GREEN : ACCENT} />
          </div>
        )}
      </div>
      {isGreen && (
        <div style={{ position: 'absolute', bottom: '-20px', right: '-20px', width: '80px', height: '80px', borderRadius: '50%', background: 'rgba(74,222,128,0.05)', filter: 'blur(20px)' }} />
      )}
    </div>
  );
};

export default function DashboardPage() {
  const { user, getHeaders, API } = useAuth();
  const [stats, setStats] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [earnings, setEarnings] = useState([]);
  const [financialCycle, setFinancialCycle] = useState(null);
  const [visualTree, setVisualTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 768;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const media = window.matchMedia('(max-width: 768px)');
    const handleChange = (event) => setIsMobile(event.matches);
    setIsMobile(media.matches);
    if (media.addEventListener) {
      media.addEventListener('change', handleChange);
      return () => media.removeEventListener('change', handleChange);
    }
    media.addListener(handleChange);
    return () => media.removeListener(handleChange);
  }, []);

  const referralLink = `${window.location.origin}/ref/${user?.referral_code}`;
  const fetchData = useCallback(async () => {
    try {
      const [statsRes, refsRes, earnsRes, cycleRes, treeRes] = await Promise.all([
        axios.get(`${API}/user/stats`, { headers: getHeaders() }),
        axios.get(`${API}/user/referrals`, { headers: getHeaders() }),
        axios.get(`${API}/user/earnings`, { headers: getHeaders() }),
        axios.get(`${API}/user/financial-cycle`, { headers: getHeaders() }),
        axios.get(`${API}/user/visual-tree`, { headers: getHeaders() }),
      ]);
      setStats(statsRes.data);
      setReferrals(refsRes.data);
      setEarnings(earnsRes.data);
      setFinancialCycle(cycleRes.data);
      setVisualTree(treeRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [API, getHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const recentEarningsChart = earnings.slice(0, 8).map((e, i) => ({
    name: e.referred_user_name?.split(' ')[0] || `Ref ${i + 1}`,
    value: parseFloat(e.amount),
  })).reverse();
  const activeCycle = financialCycle?.cycle || stats?.active_cycle;
  const cyclePositions = financialCycle?.positions || [];
  const visualNodes = visualTree?.nodes || [];
  const directReferrals = stats?.direct_referrals || referrals || [];
  const directActiveCount = stats?.direct_active_referrals_count ?? directReferrals.filter(ref => ref.investment_status === 'active').length;
  const directGoal = stats?.direct_referrals_goal || 2;
  const directCompleted = stats?.direct_referrals_completed ?? directActiveCount >= directGoal;
  const paidPositions = activeCycle?.paid_positions_count || 0;
  const cycleCapacity = activeCycle?.capacity || 14;
  const cycleProgressPct = Math.min(activeCycle?.progress_pct || 0, 100);
  const remainingPositions = activeCycle?.remaining_positions ?? Math.max(0, cycleCapacity - paidPositions);
  const cycleStatus = activeCycle?.status === 'completed' ? 'Completo' : 'Abierto';
  const withdrawalMessage = paidPositions >= cycleCapacity
    ? 'Ciclo completo. Puedes retirar el saldo disponible.'
    : paidPositions >= 7
      ? 'Retiro disponible. Los pagos se procesan los viernes.'
      : 'Tu retiro estará disponible cuando completes 7 posiciones de tu red.';
  const projectionData = Array.from({ length: 15 }, (_, i) => ({
    positions: i,
    earnings: i === 0 ? 0 : (i * 42.85) + (i >= 7 ? 0.05 : 0) + (i >= 14 ? 0.05 : 0),
    label: `${i} posiciones`,
  }));

  const copyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', background: BG, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
        <div style={{ width: '40px', height: '40px', border: '2px solid #9EAFAC', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <span style={{ color: ACCENT_DARK, fontSize: '13px' }}>Cargando billetera...</span>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: BG, color: 'white', paddingTop: '64px' }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse-glow { 0%,100%{ box-shadow: 0 0 8px rgba(74,222,128,0.3); } 50%{ box-shadow: 0 0 20px rgba(74,222,128,0.6); } }
        @keyframes float { 0%,100%{ transform: translateY(0px); } 50%{ transform: translateY(-4px); } }
        @keyframes shimmer { 0%{ background-position: -200% 0; } 100%{ background-position: 200% 0; } }
        @keyframes fadeUp { from{ opacity:0; transform:translateY(12px); } to{ opacity:1; transform:translateY(0); } }
        @keyframes countPulse { 0%{ transform: scale(1); } 50%{ transform: scale(1.04); } 100%{ transform: scale(1); } }
        .earning-row:hover { background: rgba(74,222,128,0.04) !important; transition: background 0.2s; }
        .referral-item:hover { background: rgba(158,175,172,0.05) !important; }
      `}</style>
      <Navbar />

      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: isMobile ? '24px 14px' : '32px 24px' }}>

        {/* ── Header ──────────────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '32px', flexWrap: 'wrap', gap: '12px', animation: 'fadeUp 0.5s ease both' }}>
          <div>
            <p style={{ color: ACCENT_DARK, fontSize: '14px', margin: '0 0 4px' }}>Bienvenido,</p>
            <h1 style={{ color: 'white', fontFamily: 'Outfit, sans-serif', fontSize: '26px', fontWeight: 700, margin: 0 }}>{user?.full_name}</h1>
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: activeCycle?.status === 'completed' ? `${GREEN}14` : `${ACCENT}14`,
            border: `1px solid ${activeCycle?.status === 'completed' ? GREEN_BORDER : 'rgba(158,175,172,0.25)'}`,
            borderRadius: '50px',
            padding: '8px 16px',
            animation: 'float 3s ease-in-out infinite',
          }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: activeCycle?.status === 'completed' ? GREEN : ACCENT, boxShadow: `0 0 8px ${activeCycle?.status === 'completed' ? GREEN : ACCENT}` }} />
            <span style={{ fontSize: '14px', fontWeight: 600, color: 'white' }}>Ciclo {cycleStatus.toLowerCase()} · {paidPositions}/{cycleCapacity}</span>
          </div>
        </div>

        {/* ── Stats row ────────────────────────────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: isMobile ? '10px' : '14px', marginBottom: '24px' }}>
          <AnimatedStatCard label="Inversión" value={stats?.total_investment || 0} prefix="$" color={ACCENT} icon={Wallet} delay={0} />
          <AnimatedStatCard label="Ganancias Totales" value={stats?.total_earnings || 0} prefix="$" color={GREEN} testid="total-earnings" icon={DollarSign} glowColor={GREEN} delay={100} />
          <AnimatedStatCard label="Directos Activos" value={directActiveCount} color={GREEN} testid="total-referrals" icon={Users} glowColor={GREEN} delay={200} />
          <AnimatedStatCard label="Saldo Disponible" value={stats?.available_balance || 0} prefix="$" color={GREEN} testid="available-balance" icon={TrendingUp} glowColor={GREEN} delay={300} />
        </div>

        {/* ── Main grid ────────────────────────────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr', gap: isMobile ? '14px' : '20px', marginBottom: '24px' }}>

          {/* Financial cycle card */}
          <div
            data-testid="financial-cycle-card"
            style={{
              background: 'rgba(19,20,19,0.9)',
              border: `1px solid ${activeCycle?.status === 'completed' ? GREEN_BORDER : 'rgba(158,175,172,0.18)'}`,
              borderRadius: '20px',
              padding: isMobile ? '20px 16px' : '24px',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 0 30px rgba(158,175,172,0.08)',
            }}
          >
            <div style={{ position: 'absolute', top: '20%', left: '50%', transform: 'translateX(-50%)', width: '140px', height: '140px', borderRadius: '50%', background: 'rgba(158,175,172,0.06)', filter: 'blur(30px)', pointerEvents: 'none' }} />
            <p style={{ color: ACCENT_DARK, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: isMobile ? '14px' : '20px' }}>Ciclo Financiero</p>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '8px 0' }}>
              <div style={{ width: isMobile ? '92px' : '104px', height: isMobile ? '92px' : '104px', borderRadius: '50%', border: `3px solid ${activeCycle?.status === 'completed' ? GREEN_BORDER : 'rgba(158,175,172,0.28)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', background: activeCycle?.status === 'completed' ? GREEN_DIM : 'rgba(158,175,172,0.08)', boxShadow: '0 0 22px rgba(158,175,172,0.14)', marginBottom: '16px' }}>
                <Activity size={isMobile ? 34 : 40} color={activeCycle?.status === 'completed' ? GREEN : ACCENT} />
              </div>
              <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: isMobile ? '28px' : '24px', fontWeight: 800, color: 'white', marginTop: '8px', marginBottom: '4px', lineHeight: 1.1 }}>{paidPositions}/{cycleCapacity} posiciones</h3>
              <p style={{ color: activeCycle?.status === 'completed' ? GREEN : ACCENT, fontSize: '13px', fontWeight: 700 }}>{cycleStatus}</p>
              <div style={{ width: '100%', marginTop: isMobile ? '14px' : '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: ACCENT_DARK, marginBottom: '6px', gap: '8px' }}>
                  <span>Progreso del ciclo</span>
                  <span style={{ color: ACCENT, textAlign: 'right' }}>{cycleProgressPct.toFixed(0)}%</span>
                </div>
                <div style={{ height: '6px', background: 'rgba(255,255,255,0.05)', borderRadius: '50px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${cycleProgressPct}%`, background: `linear-gradient(90deg, ${ACCENT}, ${GREEN})`, borderRadius: '50px', transition: 'width 1s ease', boxShadow: `0 0 8px ${GREEN}60` }} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '14px' }}>
                  <div style={{ background: 'rgba(158,175,172,0.05)', border: '1px solid rgba(158,175,172,0.1)', borderRadius: '10px', padding: '10px' }}>
                    <div style={{ color: ACCENT_DARK, fontSize: '10px', textTransform: 'uppercase' }}>Ganancia</div>
                    <div style={{ color: GREEN, fontWeight: 800 }}>${parseFloat(activeCycle?.user_earnings || 0).toFixed(2)}</div>
                  </div>
                  <div style={{ background: 'rgba(158,175,172,0.05)', border: '1px solid rgba(158,175,172,0.1)', borderRadius: '10px', padding: '10px' }}>
                    <div style={{ color: ACCENT_DARK, fontSize: '10px', textTransform: 'uppercase' }}>Faltan</div>
                    <div style={{ color: ACCENT, fontWeight: 800 }}>{remainingPositions}</div>
                  </div>
                </div>
                <p style={{ color: activeCycle?.status === 'completed' ? GREEN : ACCENT_DARK, fontSize: '12px', lineHeight: 1.45, marginTop: '12px', marginBottom: 0 }}>{withdrawalMessage}</p>
              </div>
            </div>
          </div>

          {/* Cycle projection chart */}
          <div style={{ background: 'rgba(19,20,19,0.9)', border: '1px solid rgba(158,175,172,0.1)', borderRadius: '20px', padding: isMobile ? '18px 14px' : '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '6px' : 0, marginBottom: isMobile ? '14px' : '20px' }}>
              <p style={{ color: ACCENT_DARK, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.15em', margin: 0 }}>Ciclo Activo</p>
              <span style={{ color: ACCENT, fontSize: '12px' }}>{activeCycle?.paid_positions_count || 0}/{activeCycle?.capacity || 14} posiciones · Meta: $600 · Retiro desde 7/14</span>
            </div>
            <ResponsiveContainer width="100%" height={isMobile ? 210 : 200}>
              <AreaChart data={projectionData} margin={isMobile ? { top: 4, right: 4, left: -20, bottom: 0 } : { top: 4, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="earningsGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4ade80" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#4ade80" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="positions" stroke="#374151" tick={{ fill: ACCENT_DARK, fontSize: isMobile ? 10 : 11 }} tickLine={false} minTickGap={isMobile ? 18 : 8} interval={isMobile ? 2 : 0} />
                <YAxis stroke="#374151" tick={{ fill: ACCENT_DARK, fontSize: isMobile ? 10 : 11 }} width={isMobile ? 34 : 42} tickLine={false} axisLine={false} tickFormatter={v => `$${v}`} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="earnings" stroke="#4ade80" strokeWidth={2} fill="url(#earningsGrad)" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
            <div style={{ marginTop: '14px', display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', gap: '12px', background: 'rgba(74,222,128,0.05)', border: '1px solid rgba(74,222,128,0.15)', borderRadius: '12px', padding: isMobile ? '12px 10px' : '12px' }}>
              <div style={{ width: '10px', height: '10px', background: GREEN, borderRadius: '50%', boxShadow: `0 0 8px ${GREEN}`, animation: 'pulse-glow 2s infinite' }} />
              <span style={{ color: ACCENT, fontSize: isMobile ? '13px' : '14px', lineHeight: 1.35 }}>
                Ciclo activo: <span style={{ color: 'white', fontWeight: 600 }}>{activeCycle?.paid_positions_count || 0} posiciones · <span style={{ color: GREEN }}>${parseFloat(activeCycle?.user_earnings || 0).toFixed(2)}</span> acumulados</span>
              </span>
            </div>
          </div>
        </div>

        {/* ── Bottom row ───────────────────────────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : (earnings.length > 0 ? '2fr 1fr' : '1fr'), gap: isMobile ? '14px' : '20px', marginBottom: '24px' }}>

          {/* Recent earnings */}
          {earnings.length > 0 && (
            <div style={{ background: 'rgba(19,20,19,0.9)', border: '1px solid rgba(158,175,172,0.1)', borderRadius: '20px', padding: '24px' }}>
              <p style={{ color: ACCENT_DARK, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: '20px' }}>Últimas Ganancias</p>
              {recentEarningsChart.length > 0 && (
                <ResponsiveContainer width="100%" height={110}>
                  <BarChart data={recentEarningsChart}>
                    <XAxis dataKey="name" stroke="#374151" tick={{ fill: ACCENT_DARK, fontSize: 10 }} tickLine={false} />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(74,222,128,0.04)' }} />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                      {recentEarningsChart.map((_, i) => (
                        <Cell key={i} fill={i === recentEarningsChart.length - 1 ? GREEN : `${GREEN}70`} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
              <div data-testid="earnings-list" style={{ marginTop: '14px' }}>
                {earnings.slice(0, 5).map((e, i) => (
                  <div
                    key={i}
                    className="earning-row"
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '11px 8px', borderBottom: i < 4 ? '1px solid rgba(158,175,172,0.07)' : 'none',
                      borderRadius: '8px',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '34px', height: '34px', borderRadius: '50%',
                        background: GREEN_DIM,
                        border: `1px solid ${GREEN_BORDER}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <span style={{ color: GREEN, fontSize: '13px', fontWeight: 700 }}>{e.referred_user_name?.charAt(0)?.toUpperCase()}</span>
                      </div>
                      <div>
                        <div style={{ color: 'white', fontSize: '14px', fontWeight: 500 }}>{e.referred_user_name}</div>
                        <div style={{ color: ACCENT_DARK, fontSize: '12px' }}>{new Date(e.created_at).toLocaleDateString('es-ES')}</div>
                      </div>
                    </div>
                    <div style={{
                      color: GREEN,
                      fontFamily: 'Outfit, sans-serif',
                      fontWeight: 700,
                      fontSize: '15px',
                      textShadow: `0 0 10px rgba(74,222,128,0.4)`,
                    }}>+${parseFloat(e.amount).toFixed(2)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Referral link - only shown after payment (active status) */}
          <div style={{ background: 'rgba(19,20,19,0.9)', border: '1px solid rgba(158,175,172,0.1)', borderRadius: '20px', padding: '24px' }}>
            <p style={{ color: ACCENT_DARK, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: '14px' }}>Tu Enlace de Referido</p>

            {user?.investment_status === 'active' ? (
              <>
                <p style={{ color: ACCENT, fontSize: '14px', marginBottom: '14px', lineHeight: 1.5 }}>Comparte siempre tu link personal. Tus 2 invitados deben registrarse desde este enlace.</p>
                <div style={{ background: 'rgba(158,175,172,0.05)', border: '1px solid rgba(158,175,172,0.15)', borderRadius: '10px', padding: '11px 13px', marginBottom: '10px' }}>
                  <code data-testid="referral-link" style={{ color: ACCENT, fontSize: '12px', wordBreak: 'break-all' }}>{referralLink}</code>
                </div>
                <button
                  onClick={copyLink}
                  data-testid="dashboard-copy-referral"
                  style={{
                    width: '100%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    padding: '12px',
                    background: copied ? GREEN_DIM : 'rgba(158,175,172,0.08)',
                    border: `1px solid ${copied ? GREEN_BORDER : 'rgba(158,175,172,0.35)'}`,
                    borderRadius: '10px',
                    color: copied ? GREEN : ACCENT,
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: '14px',
                    fontFamily: 'Inter, sans-serif',
                    boxShadow: copied ? `0 0 18px rgba(74,222,128,0.22)` : '0 0 14px rgba(158,175,172,0.1)',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                  {copied ? '¡Copiado!' : 'Copiar Enlace'}
                </button>

              </>
            ) : (
              /* Locked state - investment not yet active */
              <div style={{ textAlign: 'center', padding: '24px 16px' }}>
                <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'rgba(158,175,172,0.06)', border: '1px solid rgba(158,175,172,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                  <span style={{ fontSize: '22px' }}>🔒</span>
                </div>
                <p style={{ color: ACCENT, fontSize: '14px', fontWeight: 600, marginBottom: '6px' }}>Enlace disponible tras pago</p>
                <p style={{ color: ACCENT_DARK, fontSize: '13px', lineHeight: 1.5 }}>Una vez confirmada tu inversión de <span style={{ color: ACCENT }}>$100</span>, se activa tu ciclo de 14 posiciones.</p>
              </div>
            )}
          </div>

          <DirectNetworkCard
            directReferrals={directReferrals}
            activeCount={directActiveCount}
            goal={directGoal}
            completed={directCompleted}
            isMobile={isMobile}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '14px' : '20px', marginBottom: '24px' }}>
          <div style={{ background: 'rgba(19,20,19,0.9)', border: '1px solid rgba(158,175,172,0.1)', borderRadius: '20px', padding: '24px' }}>
            <p style={{ color: ACCENT_DARK, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: '16px' }}>Posiciones Financieras</p>
            {cyclePositions.length === 0 && (
              <div style={{ background: 'rgba(158,175,172,0.04)', border: '1px solid rgba(158,175,172,0.1)', borderRadius: '12px', padding: '14px', marginBottom: '12px' }}>
                <p style={{ color: ACCENT, fontSize: '14px', marginBottom: '6px' }}>Aún no hay posiciones pagadas en tu ciclo financiero.</p>
                <p style={{ color: ACCENT_DARK, fontSize: '13px', lineHeight: 1.5, margin: 0 }}>Tus invitados directos aparecen en Mi red directa. Las posiciones financieras se llenan a medida que la red sigue creciendo por niveles.</p>
              </div>
            )}
            {cyclePositions.slice(0, 14).map(pos => (
              <div key={pos.position_number} style={{ display: 'grid', gridTemplateColumns: '44px 1fr auto', gap: '10px', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid rgba(158,175,172,0.07)' }}>
                <span style={{ color: ACCENT, fontWeight: 800 }}>#{pos.position_number}</span>
                <div>
                  <div style={{ color: 'white', fontSize: '14px', fontWeight: 600 }}>{pos.payer_name || 'Pendiente'}</div>
                  <div style={{ color: ACCENT_DARK, fontSize: '12px' }}>{pos.payer_referral_code || 'Sin pago confirmado'} · depth {pos.visual_depth || 0}</div>
                </div>
                <span style={{ color: GREEN, fontWeight: 800 }}>${parseFloat(pos.user_share || 0).toFixed(2)}</span>
              </div>
            ))}
          </div>

          <div style={{ background: 'rgba(19,20,19,0.9)', border: '1px solid rgba(158,175,172,0.1)', borderRadius: '20px', padding: '24px' }}>
            <p style={{ color: ACCENT_DARK, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: '16px' }}>Árbol Visual</p>
            <CompactVisualTree currentUser={user} directReferrals={directReferrals} visualNodes={visualNodes} />
          </div>
        </div>

      </div>
    </div>
  );
}
